import type { ScienceType } from '../types/science';
import type { ScienceBranch } from '../types/strategy';

/**
 * SF-140: pure extraction of the "Fill Strategy" distribution math, previously an inline
 * ~40-line onClick lambda in Science.tsx — mirrors buildingsPercent.ts's precedent for
 * pure-math extraction (Components §1 point 9). Distributes `unallocatedBooks` proportionally
 * across the 7 science branches per `alloc` (a strategy's `scienceAlloc`, percentages that need
 * not sum to 100), with any leftover-from-flooring remainder given to the first branch with a
 * positive allocation.
 */

const SCIENCE_TYPES: ScienceType[] = [
  'ALCHEMY', 'TOOLS', 'HOUSING', 'FOOD', 'MILITARY', 'THIEVERY', 'CHANNELING',
];

const TYPE_TO_BRANCH: Record<ScienceType, ScienceBranch> = {
  ALCHEMY: 'alchemy',
  TOOLS: 'tools',
  HOUSING: 'housing',
  FOOD: 'food',
  MILITARY: 'military',
  THIEVERY: 'thievery',
  CHANNELING: 'channeling',
};

function emptyBooks(): Record<ScienceType, number> {
  return { ALCHEMY: 0, TOOLS: 0, HOUSING: 0, FOOD: 0, MILITARY: 0, THIEVERY: 0, CHANNELING: 0 };
}

export function computeFillStrategyBooks(
  alloc: Partial<Record<ScienceBranch, number>>,
  unallocatedBooks: number
): Record<ScienceType, number> {
  const filled = emptyBooks();

  // Sum only the 7 known branches — an unknown/garbage key in `alloc` must not inflate the
  // denominator and skew every known branch's proportional share.
  const allocSum = SCIENCE_TYPES.reduce((sum, scienceType) => sum + (alloc[TYPE_TO_BRANCH[scienceType]] ?? 0), 0);
  if (allocSum <= 0 || unallocatedBooks <= 0) {
    return filled;
  }

  let remaining = unallocatedBooks;
  for (const scienceType of SCIENCE_TYPES) {
    const branch = TYPE_TO_BRANCH[scienceType];
    const pct = alloc[branch] ?? 0;
    const share = Math.floor((pct / allocSum) * unallocatedBooks);
    const capped = Math.min(share, remaining);
    filled[scienceType] = capped;
    remaining -= capped;
  }

  if (remaining > 0) {
    for (const scienceType of SCIENCE_TYPES) {
      const branch = TYPE_TO_BRANCH[scienceType];
      if ((alloc[branch] ?? 0) > 0) {
        filled[scienceType] += remaining;
        break;
      }
    }
  }

  return filled;
}
